import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Mail, Shield, UserPlus, Check, AlertTriangle, CreditCard } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { getAvailableRoles, type RoleOption } from '@/lib/rolesStore'
import {
  EXTRA_TEAM_MEMBER_COST,
  getTeamMemberLimit,
  wouldExceedTeamLimit,
} from '@/lib/teamMembersStore'
import { useToast } from '@/hooks/use-toast'
import {
  useOrganizationMembersQuery,
  useTeamInvitationsQuery,
  useInviteTeamMemberMutation,
} from '@/data/teamMembers'

type InvitableRole = 'admin' | 'manager' | 'viewer'

export default function TeamInvitePage() {
  const { hasPermission, organization, user } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  const orgId = organization?.id
  const orgName = organization?.name ?? ''
  const tier = organization?.tier ?? 'free'
  const inviterName = user?.name ?? undefined

  const membersQuery = useOrganizationMembersQuery(orgId)
  const invitationsQuery = useTeamInvitationsQuery(orgId)
  const inviteMutation = useInviteTeamMemberMutation()

  // Seat count = accepted members + pending invitations (pending invites consume a seat).
  const currentCount =
    (membersQuery.data?.length ?? 0) + (invitationsQuery.data?.length ?? 0)
  const teamLimit = getTeamMemberLimit(tier)
  const wouldExceed = wouldExceedTeamLimit(tier, currentCount)

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<InvitableRole>('viewer')
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [roleOptions] = useState<RoleOption[]>(() => getAvailableRoles())
  const [showBillingPrompt, setShowBillingPrompt] = useState(false)
  const [acceptedExtraCharge, setAcceptedExtraCharge] = useState(false)

  if (!hasPermission('manage_team')) {
    return (
      <div className="space-y-8">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-8 text-center">
          <Shield className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-destructive">Access Denied</h2>
          <p className="mt-2 text-muted-foreground">
            You don't have permission to invite team members.
          </p>
          <Button asChild className="mt-4">
            <Link to="/dashboard">Return to Dashboard</Link>
          </Button>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!orgId) {
      setError('No organization selected.')
      return
    }

    // Non-Business tiers cannot exceed the included seat limit.
    if (wouldExceed && tier !== 'business') {
      toast({
        title: 'Team limit reached',
        description: `Your ${tier} plan includes ${teamLimit} team member${teamLimit > 1 ? 's' : ''}. Upgrade to add more.`,
        variant: 'destructive',
      })
      setError(`Your ${tier} plan includes ${teamLimit} team member${teamLimit > 1 ? 's' : ''}. Please upgrade to add more team members.`)
      return
    }

    // Business tier triggers the extra-seat billing prompt before proceeding.
    if (wouldExceed && tier === 'business' && !acceptedExtraCharge) {
      setShowBillingPrompt(true)
      return
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    const existingMember = membersQuery.data?.find(
      (m) => m.user.email.toLowerCase() === email.toLowerCase(),
    )
    if (existingMember) {
      setError('A team member with this email already exists')
      return
    }
    const existingInvite = invitationsQuery.data?.find(
      (i) => i.email.toLowerCase() === email.toLowerCase(),
    )
    if (existingInvite) {
      setError('An invitation has already been sent to this email')
      return
    }

    try {
      await inviteMutation.mutateAsync({
        organizationId: orgId,
        email,
        role,
        organizationName: orgName,
        inviterName,
      })

      setSuccess(true)
      setAcceptedExtraCharge(false)

      const isExtraSeat = wouldExceed && tier === 'business'
      toast({
        title: 'Invitation sent',
        description: isExtraSeat
          ? `${email} has been invited. Extra seat charge of $${EXTRA_TEAM_MEMBER_COST}/mo will appear on your next invoice.`
          : `${email} has been invited to your team.`,
      })

      setTimeout(() => {
        navigate('/team')
      }, 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation')
    }
  }

  if (success) {
    return (
      <div className="space-y-8">
        <div className="bg-card text-card-foreground rounded-lg border p-12 text-center max-w-md mx-auto">
          <div className="h-16 w-16 bg-secondary/15 rounded-full flex items-center justify-center mx-auto">
            <Check className="h-8 w-8 text-secondary" />
          </div>
          <h2 className="mt-6 text-2xl font-bold">Invitation Sent!</h2>
          <p className="mt-4 text-muted-foreground">
            <strong>{email}</strong> has been invited to your team.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Redirecting to team page...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <Link
        to="/team"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Team
      </Link>

      <div>
        <h1 className="text-2xl font-bold">Invite Team Member</h1>
        <p className="text-muted-foreground">
          Send an invitation to join your organization
        </p>
      </div>

      <div className="bg-card text-card-foreground rounded-lg border p-8 max-w-lg">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium">
              Email address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                required
              />
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium">Role</label>
            <div className="space-y-2">
              {roleOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                    role === option.value
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={option.value}
                    checked={role === option.value}
                    onChange={(e) => setRole(e.target.value as InvitableRole)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <p className="font-medium">{option.label}</p>
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={inviteMutation.isPending}>
            <UserPlus className="mr-2 h-4 w-4" />
            {inviteMutation.isPending ? 'Sending Invitation...' : 'Send Invitation'}
          </Button>
        </form>
      </div>

      <div className="bg-muted/50 rounded-lg p-4 max-w-lg">
        <p className="text-sm text-muted-foreground">
          <strong>Note:</strong> The invited user will receive an email with instructions
          to create an account (or sign in if they already have one) and join your organization.
        </p>
      </div>

      {tier === 'business' && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 max-w-lg">
          <p className="text-sm text-muted-foreground">
            <strong>Team seats:</strong> {currentCount} of {teamLimit} included seats used.
            {currentCount >= teamLimit && (
              <span className="block mt-1">
                Additional seats are ${EXTRA_TEAM_MEMBER_COST}/month each.
              </span>
            )}
          </p>
        </div>
      )}

      {showBillingPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg border shadow-lg p-8 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 bg-accent/15 rounded-full flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Additional Team Seat</h3>
                <p className="text-sm text-muted-foreground">Extra billing required</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-muted-foreground mb-4">
                Your Business plan includes {teamLimit} team members. You currently have {currentCount} members.
              </p>
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-accent" />
                  <span className="font-medium text-foreground">Extra seat charge</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Adding this team member will incur an additional charge of <strong>${EXTRA_TEAM_MEMBER_COST}/month</strong> on your next invoice.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBillingPrompt(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setAcceptedExtraCharge(true)
                  setShowBillingPrompt(false)
                  setTimeout(() => {
                    const form = document.querySelector('form')
                    if (form) form.requestSubmit()
                  }, 0)
                }}
                className="flex-1"
              >
                Accept & Add Member
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
